import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import Loading from 'components/World/Loading';
import {
  resolveToken,
  isMockMode,
  fetchNextLesson,
} from 'services/LessonContentService';
import {
  playTutorAudio,
  pauseTutorSpeech,
  queueTutorAudioPrefetch,
  resumeTutorSpeech,
  stopTutorSpeech,
} from 'services/TextToSpeechService';
import { WEB_APP_BASE_URL } from 'config/app.config';
import {
  fetchLessonContentAction,
  advancePhase,
  setTutorialViewed,
  setWorkbookReady,
  setWorkbookDone,
  submitWorkbookAnswer,
  submitKCAnswer,
  advanceKCQuestion,
  kcSummaryDone,
  submitMasteryAnswer,
  advanceMasteryQuestion,
  jumpToDevTimelineNode,
  resetViewer,
  retakeMasteryTest,
  resetNarration,
  setNarrationPending,
  setNarrationPlaying,
  setNarrationComplete,
  setNarrationError,
  startTermReview,
  startSpeaking,
  stopSpeaking
} from 'store/lessonContentViewer/actions';
import {
  selectViewerLoading,
  selectViewerError,
  selectViewerModel,
  selectCurrentPhase,
  selectActiveTerm,
  selectActiveTermIndex,
  selectActiveLearningStepIndex,
  selectActiveQuestionIndex,
  selectActiveWorkbook,
  selectWorkbookState,
  selectActiveKCQuestion,
  selectKCAnswers,
  selectKCScore,
  selectActiveMasteryQuestion,
  selectMasteryAnswers,
  selectMasteryScore,
  selectProgressTrackerData,
  selectTutorText,
  selectNarrationTargets,
  selectShowDevTools,
  selectTutorChatOpen,
  selectFrontloadingData,
  selectMetadata,
  selectMasteryQuestions,
  selectLessonViewerUnityWorldLoading,
  selectIsNarrationGateOpen,
} from 'store/lessonContentViewer/selectors';
import LCVFrontloadingStage from './components/LCVFrontloadingStage';
import LCVStaticLessonStage from './components/LCVStaticLessonStage';
import LCVActionSidebar from './components/LCVActionSidebar';
import LCVDevStrip from './components/LCVDevStrip';
import TutorialPhase from './components/TutorialPhase';
import LearningPhase from './components/LearningPhase';
import WorkbookReadyPhase from './components/WorkbookReadyPhase';
import WorkbookOptionsPhase from './components/WorkbookOptionsPhase';
import KnowledgeCheckPhase from './components/KnowledgeCheckPhase';
import KCSummaryPhase from './components/KCSummaryPhase';
import MasteryPhase from './components/MasteryPhase';
import ResultsPhase from './components/ResultsPhase';
import LessonViewerUnityBackground from './LessonViewerUnityBackground';
import './LessonContentViewer.css';

/** Opaque black behind the progress bar so Unity / lesson UI do not show through. */
const LCV_UNITY_LOADING_BG = '#000000';
/** ~1%/s default is too slow for long Unity init; keep bar moving toward cap while overlay is up. */
const LCV_UNITY_LOADING_FAKE_RATE_PER_SEC = 9;
const MIN_TUTOR_SPEECH_DURATION_MS = 1800;
const ESTIMATED_MS_PER_WORD = 320;

function estimateTutorSpeechDurationMs(text) {
  const wordCount = String(text || '').trim().split(/\s+/).filter(Boolean).length;
  return Math.max(MIN_TUTOR_SPEECH_DURATION_MS, wordCount * ESTIMATED_MS_PER_WORD);
}

function clampProgress(value) {
  return Math.max(0, Math.min(1, value));
}

const LessonContentViewer = () => {
  const dispatch = useDispatch();

  const loading = useSelector(selectViewerLoading);
  const error = useSelector(selectViewerError);
  const viewerModel = useSelector(selectViewerModel);
  const phase = useSelector(selectCurrentPhase);
  const activeTerm = useSelector(selectActiveTerm);
  const activeTermIndex = useSelector(selectActiveTermIndex);
  const activeLearningStepIndex = useSelector(selectActiveLearningStepIndex);
  const activeQuestionIndex = useSelector(selectActiveQuestionIndex);
  const activeWorkbook = useSelector(selectActiveWorkbook);
  const workbookState = useSelector(selectWorkbookState);
  const activeKCQuestion = useSelector(selectActiveKCQuestion);
  const kcAnswers = useSelector(selectKCAnswers);
  const kcScore = useSelector(selectKCScore);
  const activeMasteryQuestion = useSelector(selectActiveMasteryQuestion);
  const masteryAnswers = useSelector(selectMasteryAnswers);
  const masteryScore = useSelector(selectMasteryScore);
  const masteryQuestions = useSelector(selectMasteryQuestions);
  const progressData = useSelector(selectProgressTrackerData);
  const tutorText = useSelector(selectTutorText);
  const narrationTargets = useSelector(selectNarrationTargets);
  const showDevTools = useSelector(selectShowDevTools);
  const tutorChatOpen = useSelector(selectTutorChatOpen);
  const frontloadingData = useSelector(selectFrontloadingData);
  const metadata = useSelector(selectMetadata);
  const unityWorldLoading = useSelector(selectLessonViewerUnityWorldLoading);
  const isNarrationGateOpen = useSelector(selectIsNarrationGateOpen);
  /**
   * Stays true after Redux clears `unityWorldLoading` until `Loading` finishes its exit animation.
   * Updated synchronously during render (no useEffect gap) so `Loading` never unmounts/remounts
   * and fake progress does not reset to a new random %.
   */
  const unityOverlayLockedRef = useRef(false);
  /** TEST: lip-sync once per lesson after Unity loading overlay dismisses. */
  const lcvTestSpeechAfterLoadRef = useRef(false);
  const [, bumpUnityOverlay] = useState(0);
  const [visualContent, setVisualContent] = useState(null);
  const [trackerMode, setTrackerMode] = useState('mini');
  const [tutorFontSize, setTutorFontSize] = useState(17);
  const [narrationPaused, setNarrationPaused] = useState(false);
  const [tutorReplayKey, setTutorReplayKey] = useState(0);
  const [nextLessonLoading, setNextLessonLoading] = useState(false);
  const [nextLessonUnavailable, setNextLessonUnavailable] = useState(false);
  const [nextLessonError, setNextLessonError] = useState('');
  const [narrationPlayback, setNarrationPlayback] = useState({
    audioId: null,
    isPlaying: false,
    currentTimeMs: 0,
    durationMs: 0,
    complete: false,
  });

  const authToken = useMemo(() => resolveToken(), []);
  /** Mock mode loads bundled JSON and does not call the API; allow without JWT. */
  const lessonFetchToken = authToken || (isMockMode() ? 'mock' : null);

  const { lessonId, skipWorld } = useMemo(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      return {
        lessonId: params.get('lessonId') || 'G3-MATH-L013-720L',
        skipWorld: params.get('skipWorld') === 'true',
      };
    }
    return { lessonId: 'G3-MATH-L013-720L', skipWorld: false };
  }, []);
  /**
   * Web + Unity: tutor TTS must not start until the world loading bar has finished
   * and exited (see `onUnityLoadingExitAnimationComplete`). Native / skipWorld: open immediately.
   */
  const [lcvUnityTutorAudioGateOpen, setLcvUnityTutorAudioGateOpen] = useState(
    () => skipWorld || Platform.OS !== 'web',
  );
  const lessonAudioScope = useMemo(
    () => (lessonId ? `lesson:${lessonId}` : 'lesson:unknown'),
    [lessonId],
  );
  const currentNarrationTarget = narrationTargets[0] || null;
  const upcomingNarrationTargets = narrationTargets.slice(1, 3);
  const upcomingNarrationSignature = upcomingNarrationTargets
    .map(target => `${target.audioId}:${target.text}`)
    .join('||');
  const tutorAnimationProgress = useMemo(() => {
    if (
      !currentNarrationTarget?.audioId ||
      narrationPlayback.audioId !== currentNarrationTarget.audioId
    ) {
      return 0;
    }

    if (narrationPlayback.complete) {
      return 1;
    }

    const durationMs =
      narrationPlayback.durationMs ||
      estimateTutorSpeechDurationMs(currentNarrationTarget.text);

    if (!durationMs) {
      return 0;
    }

    return clampProgress(narrationPlayback.currentTimeMs / durationMs);
  }, [currentNarrationTarget, narrationPlayback]);
  const handleExit = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.location.assign(WEB_APP_BASE_URL);
    }
  }, []);
  const canPauseTutor = Boolean(currentNarrationTarget?.audioId) && !narrationPlayback.complete;
  const canSkipTutor = Boolean(currentNarrationTarget?.audioId) && !narrationPlayback.complete;
  const handlePauseTutor = useCallback(() => {
    if (!canPauseTutor) return;
    if (!pauseTutorSpeech()) return;

    stopSpeaking();
    setNarrationPaused(true);
    setNarrationPlayback(current => ({
      ...current,
      isPlaying: false,
    }));
  }, [canPauseTutor]);
  const handleResumeTutor = useCallback(() => {
    resumeTutorSpeech()
      .then(resumed => {
        if (!resumed) return;
        startSpeaking();
        setNarrationPaused(false);
        setNarrationPlayback(current => ({
          ...current,
          isPlaying: true,
        }));
      })
      .catch(() => {
        // noop - existing narration error handling covers playback failures
      });
  }, []);
  const handleSkipTutor = useCallback(() => {
    if (!canSkipTutor || !currentNarrationTarget?.audioId) return;

    stopTutorSpeech();
    stopSpeaking();
    setNarrationPaused(false);
    setNarrationPlayback(current => {
      if (current.audioId !== currentNarrationTarget.audioId) return current;

      const resolvedDurationMs =
        current.durationMs ||
        estimateTutorSpeechDurationMs(currentNarrationTarget.text);

      return {
        ...current,
        isPlaying: false,
        currentTimeMs: resolvedDurationMs,
        durationMs: resolvedDurationMs,
        complete: true,
      };
    });
    dispatch(setNarrationComplete(currentNarrationTarget.audioId));
  }, [
    canSkipTutor,
    currentNarrationTarget?.audioId,
    currentNarrationTarget?.text,
    dispatch,
  ]);
  const handleReplayTutor = useCallback(() => {
    if (!currentNarrationTarget?.audioId) return;

    stopTutorSpeech();
    stopSpeaking();
    setNarrationPaused(false);
    setTutorReplayKey(current => current + 1);
  }, [currentNarrationTarget?.audioId]);

  const buildNextLessonUrl = useCallback(nextLessonId => {
    if (typeof window === 'undefined') return null;

    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.set('screen', 'lessonViewer');
    nextUrl.searchParams.set('lessonId', nextLessonId);
    return nextUrl.toString();
  }, []);

  const handleGoToNextLesson = useCallback(async () => {
    if (!lessonFetchToken || nextLessonLoading) return;

    setNextLessonLoading(true);
    setNextLessonUnavailable(false);
    setNextLessonError('');

    try {
      const response = await fetchNextLesson(lessonId, lessonFetchToken);
      const nextLessonId = String(response?.lessonId || '').trim();

      if (!nextLessonId || nextLessonId.toLowerCase() === 'no more lessons') {
        setNextLessonUnavailable(true);
        return;
      }

      const nextLessonUrl = buildNextLessonUrl(nextLessonId);
      if (!nextLessonUrl) {
        throw new Error('Unable to build the next lesson URL.');
      }

      window.location.assign(nextLessonUrl);
    } catch (nextLessonFetchError) {
      setNextLessonError(
        nextLessonFetchError?.message || 'Unable to open the next lesson right now.',
      );
    } finally {
      setNextLessonLoading(false);
    }
  }, [buildNextLessonUrl, lessonFetchToken, lessonId, nextLessonLoading]);

  const handleReviewTerm = useCallback(
    termIndex => {
      setNextLessonUnavailable(false);
      setNextLessonError('');
      dispatch(startTermReview(termIndex));
    },
    [dispatch],
  );

  const handleRetakeMastery = useCallback(() => {
    setNextLessonUnavailable(false);
    setNextLessonError('');
    dispatch(retakeMasteryTest());
  }, [dispatch]);

  useEffect(() => {
    if (!lessonFetchToken) return;
    dispatch(fetchLessonContentAction(lessonId, lessonFetchToken, { skipWorld }));
    return () => dispatch(resetViewer());
  }, [dispatch, lessonId, lessonFetchToken, skipWorld]);

  useEffect(() => {
    setVisualContent(null);
  }, [lessonId]);

  useEffect(() => {
    setNextLessonLoading(false);
    setNextLessonUnavailable(false);
    setNextLessonError('');
  }, [lessonId]);

  useEffect(() => {
    unityOverlayLockedRef.current = false;
    lcvTestSpeechAfterLoadRef.current = false;
    setLcvUnityTutorAudioGateOpen(skipWorld || Platform.OS !== 'web');
    bumpUnityOverlay(n => n + 1);
  }, [lessonId, skipWorld]);

  useEffect(() => {
    setNarrationPaused(false);
  }, [currentNarrationTarget?.audioId]);

  const onUnityLoadingExitAnimationComplete = useCallback(() => {
    unityOverlayLockedRef.current = false;
    setLcvUnityTutorAudioGateOpen(true);
    bumpUnityOverlay(n => n + 1);
    if (
      Platform.OS === 'web' &&
      !skipWorld &&
      !lcvTestSpeechAfterLoadRef.current
    ) {
      lcvTestSpeechAfterLoadRef.current = true;


      //TEST uncomment this line to make the tutor "speak" for 30 seconds after the loading screen.
      //startSpeaking(30_000);

    }
  }, [skipWorld]);

  useEffect(() => {
    if (!currentNarrationTarget?.audioId || !currentNarrationTarget?.text) {
      stopTutorSpeech();
      stopSpeaking();
      setNarrationPlayback({
        audioId: null,
        isPlaying: false,
        currentTimeMs: 0,
        durationMs: 0,
        complete: false,
      });
      dispatch(resetNarration());
      return undefined;
    }

    if (!lcvUnityTutorAudioGateOpen) {
      return undefined;
    }

    let cancelled = false;
    const fallbackDurationMs = estimateTutorSpeechDurationMs(currentNarrationTarget.text);

    setNarrationPlayback({
      audioId: currentNarrationTarget.audioId,
      isPlaying: false,
      currentTimeMs: 0,
      durationMs: fallbackDurationMs,
      complete: false,
    });
    dispatch(setNarrationPending(currentNarrationTarget.audioId));

    playTutorAudio({
      lessonScope: lessonAudioScope,
      audioId: currentNarrationTarget.audioId,
      text: currentNarrationTarget.text,
      onPlaybackStart: playback => {
        if (cancelled) return;
        setNarrationPlayback(current => {
          if (current.audioId !== currentNarrationTarget.audioId) return current;

          return {
            ...current,
            isPlaying: true,
            durationMs: playback?.durationMs || current.durationMs,
          };
        });
        dispatch(setNarrationPlaying(currentNarrationTarget.audioId));
        startSpeaking();
        queueTutorAudioPrefetch({
          lessonScope: lessonAudioScope,
          entries: upcomingNarrationTargets,
        });
      },
      onPlaybackProgress: playback => {
        if (cancelled) return;
        setNarrationPlayback(current => {
          if (current.audioId !== currentNarrationTarget.audioId) return current;

          return {
            ...current,
            currentTimeMs: playback?.currentTimeMs || 0,
            durationMs: playback?.durationMs || current.durationMs,
          };
        });
      },
      onPlaybackStop: () => {
        stopSpeaking();
        setNarrationPlayback(current => {
          if (current.audioId !== currentNarrationTarget.audioId) return current;

          return {
            ...current,
            isPlaying: false,
          };
        });
      },
    })
      .then(() => {
        if (cancelled) return;
        setNarrationPlayback(current => {
          if (current.audioId !== currentNarrationTarget.audioId) return current;

          const resolvedDurationMs = current.durationMs || fallbackDurationMs;

          return {
            ...current,
            isPlaying: false,
            currentTimeMs: resolvedDurationMs,
            durationMs: resolvedDurationMs,
            complete: true,
          };
        });
        dispatch(setNarrationComplete(currentNarrationTarget.audioId));
      })
      .catch(error => {
        if (cancelled) return;
        stopSpeaking();
        setNarrationPlayback(current => {
          if (current.audioId !== currentNarrationTarget.audioId) return current;

          const resolvedDurationMs = current.durationMs || fallbackDurationMs;

          return {
            ...current,
            isPlaying: false,
            currentTimeMs: resolvedDurationMs,
            durationMs: resolvedDurationMs,
            complete: true,
          };
        });
        dispatch(
          setNarrationError(
            currentNarrationTarget.audioId,
            error?.message || 'Failed to generate tutor audio',
          ),
        );
      });

    return () => {
      cancelled = true;
      stopTutorSpeech();
    };
  }, [
    dispatch,
    currentNarrationTarget?.audioId,
    currentNarrationTarget?.text,
    lessonAudioScope,
    tutorReplayKey,
    upcomingNarrationSignature,
    lcvUnityTutorAudioGateOpen,
  ]);

  useEffect(() => {
    if (phase === 'frontloading') {
      setTrackerMode('full');
      return undefined;
    }

    if (phase === 'knowledge_check_summary') {
      setTrackerMode('full');
      const collapseTimer = window.setTimeout(() => {
        setTrackerMode('mini');
      }, 5000);
      return () => window.clearTimeout(collapseTimer);
    }

    setTrackerMode('mini');
    return undefined;
  }, [phase]);

  const showShell =
    phase !== 'tutorial' && !loading && !error && viewerModel;
  const useFrontloadingStage =
    showShell && phase === 'frontloading' && progressData && frontloadingData;
  const useStaticStage =
    showShell && phase !== 'frontloading';

  if (unityWorldLoading) {
    unityOverlayLockedRef.current = true;
  }

  const showUnityLoadingOverlay =
    !skipWorld &&
    Platform.OS === 'web' &&
    (unityWorldLoading || unityOverlayLockedRef.current);

  if (!lessonFetchToken) {
    return (
      <div className="lcv-root lcv-auth-gate">
        <div className="lcv-auth-modal">
          <div className="lcv-auth-icon">🔒</div>
          <h2 className="lcv-auth-title">Access Required</h2>
          <p className="lcv-auth-message">
            This lesson is not available at this address.
            Please use the link provided to you to access your lesson.
          </p>
          <a
            href={WEB_APP_BASE_URL}
            className="lcv-btn-primary lcv-auth-link"
            rel="noopener noreferrer">
            Go to QuikLearn
          </a>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="lcv-root">
        <div className="lcv-loading">
          <div className="lcv-spinner" />
          <p>Loading lesson...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="lcv-root">
        <div className="lcv-error">
          <span className="lcv-error-icon">!</span>
          <h2>Unable to load lesson</h2>
          <p>{error}</p>
          <button
            className="lcv-btn-primary"
            onClick={() =>
              dispatch(fetchLessonContentAction(lessonId, lessonFetchToken, { skipWorld }))
            }>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!viewerModel) return null;

  return (
    <div className={`lcv-root ${tutorChatOpen ? 'lcv-tutor-active' : ''}`}>
      {!skipWorld && <LessonViewerUnityBackground />}

      {showUnityLoadingOverlay && (
        <div
          className="lcv-unity-world-loading"
          aria-busy={unityWorldLoading}
          aria-live="polite">
          <Loading
            loading={unityWorldLoading}
            color={LCV_UNITY_LOADING_BG}
            label="Loading world…"
            fakeRatePerSecond={LCV_UNITY_LOADING_FAKE_RATE_PER_SEC}
            onExitAnimationComplete={onUnityLoadingExitAnimationComplete}
          />
        </div>
      )}

      {showShell && (
        <LCVActionSidebar onExit={handleExit} />
      )}

      {phase === 'tutorial' && (
        <TutorialPhase onComplete={() => dispatch(setTutorialViewed())} />
      )}

      {useFrontloadingStage && (
        <LCVFrontloadingStage
          data={frontloadingData}
          tutorText={tutorText}
          metadata={metadata}
          progressData={progressData}
          phase={phase}
          trackerMode={trackerMode}
          visualContent={visualContent}
          startDisabled={!isNarrationGateOpen}
          tutorAnimationProgress={tutorAnimationProgress}
          tutorFontSize={tutorFontSize}
          onTutorFontSizeChange={setTutorFontSize}
          onPauseTutor={handlePauseTutor}
          onResumeTutor={handleResumeTutor}
          onSkipTutor={handleSkipTutor}
          canPauseTutor={canPauseTutor}
          canSkipTutor={canSkipTutor}
          onStart={() => {
            if (isNarrationGateOpen) {
              dispatch(advancePhase());
            }
          }}
          paused={tutorChatOpen || narrationPaused}
        />
      )}

      {useStaticStage && (
        <LCVStaticLessonStage
          metadata={metadata}
          progressData={progressData}
          phase={phase}
          activeTerm={activeTerm}
          tutorText={tutorText}
          tutorAnimationProgress={tutorAnimationProgress}
          tutorPaused={narrationPaused}
          canPauseTutor={canPauseTutor}
          tutorFontSize={tutorFontSize}
          onTutorFontSizeChange={setTutorFontSize}
          onPauseTutor={handlePauseTutor}
          onResumeTutor={handleResumeTutor}
          onSkipTutor={handleSkipTutor}
          onReplayTutor={handleReplayTutor}
          trackerMode={trackerMode}
          canReplayTutor={Boolean(currentNarrationTarget?.audioId) && narrationPlayback.complete}
          canSkipTutor={canSkipTutor}
          visualContent={visualContent}
          middleContent={
            <>
              {phase === 'learning' && activeTerm && (
                <LearningPhase
                  term={activeTerm}
                  termIndex={activeTermIndex}
                  continueLabel={
                    (activeTerm?.learningSteps?.length || 0) > activeLearningStepIndex + 1
                      ? 'Continue →'
                      : 'Ready for the Workbook →'
                  }
                  continueDisabled={!isNarrationGateOpen}
                  onContinue={() => dispatch(advancePhase())}
                />
              )}

              {(phase === 'workbook_ready' || phase === 'workbook_exercise') &&
                activeTerm && (
                  <WorkbookReadyPhase
                    workbook={activeWorkbook}
                    workbookState={workbookState}
                    navigationDisabled={!isNarrationGateOpen}
                    onReady={() => dispatch(setWorkbookReady())}
                    onDone={() => dispatch(setWorkbookDone())}
                  />
                )}

              {phase === 'workbook_options' && activeTerm && (
                <WorkbookOptionsPhase
                  workbook={activeWorkbook}
                  workbookState={workbookState}
                  continueDisabled={!isNarrationGateOpen}
                  onSubmit={idx => dispatch(submitWorkbookAnswer(idx))}
                  onContinue={() => dispatch(advancePhase())}
                />
              )}

              {phase === 'knowledge_check' && activeKCQuestion && (
                <KnowledgeCheckPhase
                  question={activeKCQuestion}
                  questionIndex={activeQuestionIndex}
                  totalQuestions={activeTerm?.knowledgeCheck?.questions?.length || 0}
                  kcAnswers={kcAnswers}
                  nextDisabled={!isNarrationGateOpen}
                  onSubmit={answer => dispatch(submitKCAnswer(answer))}
                  onNext={() => dispatch(advanceKCQuestion())}
                />
              )}

              {phase === 'knowledge_check_summary' && (
                <KCSummaryPhase
                  score={kcScore}
                  termName={activeTerm?.term}
                  continueDisabled={!isNarrationGateOpen}
                  onContinue={() => dispatch(kcSummaryDone())}
                />
              )}

              {phase === 'mastery' && activeMasteryQuestion && (
                <MasteryPhase
                  question={activeMasteryQuestion}
                  questionIndex={activeQuestionIndex}
                  totalQuestions={masteryQuestions.length}
                  masteryAnswers={masteryAnswers}
                  nextDisabled={!isNarrationGateOpen}
                  onSubmit={answer => dispatch(submitMasteryAnswer(answer))}
                  onNext={() => dispatch(advanceMasteryQuestion())}
                />
              )}

              {phase === 'results' && (
                <ResultsPhase
                  score={masteryScore}
                  terms={viewerModel.terms}
                  masteryAnswers={masteryAnswers}
                  nextLessonLoading={nextLessonLoading}
                  nextLessonUnavailable={nextLessonUnavailable}
                  nextLessonError={nextLessonError}
                  onReviewTerm={handleReviewTerm}
                  onGoToNextLesson={handleGoToNextLesson}
                  onDoneNavigate={handleExit}
                  onRetakeMastery={handleRetakeMastery}
                />
              )}
            </>
          }
        />
      )}

      {showDevTools && viewerModel && (
        <LCVDevStrip
          metadata={metadata}
          phase={phase}
          activeTermIndex={activeTermIndex}
          activeQuestionIndex={activeQuestionIndex}
          terms={viewerModel.terms}
          masteryQuestionCount={masteryQuestions.length}
          onJump={node => dispatch(jumpToDevTimelineNode(node))}
        />
      )}
    </div>
  );
};

export default LessonContentViewer;
